'use client'

import { PreviewProductsModalContent } from '@/components/common/collection/collection-mix-match/collection-mix-match-item'
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalDescription,
  ResponsiveModalTitle,
  ResponsiveModalTrigger,
} from '@/components/extension/responsive-modal'
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn } from '@/lib/utils'
import Autoplay from 'embla-carousel-autoplay'
import { ArrowRight } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useEffect, useState } from 'react'
import { DETAIL_ITEMS, DETAILS_SECTION_ID, TRACKING_PREFIX } from './constants'
import {
  IMAGE_GRADIENT_CLASS,
  SECTION_TITLE_CLASS,
  SECTION_X_CLASS,
} from './ui'

const TRACKING = `${TRACKING_PREFIX}__detail`

const NAV_BUTTON_CLASS =
  'absolute top-1/2 size-12 -translate-y-1/2 rounded-full border-none bg-white text-black shadow-[0_4px_9px_rgba(0,0,0,0.12)] hover:bg-white'

export function DetailFeatures() {
  const t = useTranslations('do_thu_dong_hardcode.details')
  const [api, setApi] = useState<CarouselApi>()
  const { thumbWidth, thumbOffset } = useScrollThumb(api)

  return (
    <section
      id={DETAILS_SECTION_ID}
      className={cn(
        'flex scroll-mt-20 flex-col gap-5 overflow-hidden py-10 lg:gap-10 lg:py-20',
        SECTION_X_CLASS
      )}
    >
      <h2 className={SECTION_TITLE_CLASS}>{t('title')}</h2>

      {/* Desktop: 4 card vừa khung nội dung, card thứ 5 tràn ra mép phải màn hình */}
      <Carousel
        setApi={setApi}
        opts={{ align: 'start', loop: true }}
        plugins={[
          Autoplay({
            delay: 4000,
            stopOnInteraction: false,
            stopOnMouseEnter: true,
          }),
        ]}
        className='-mr-4 lg:-mr-15'
      >
        <CarouselContent className='-ml-3 lg:-ml-5'>
          {DETAIL_ITEMS.map((item, index) => {
            const title = t(`items.${index}`)
            return (
              <CarouselItem
                key={item.image}
                className='basis-[272px] pl-3 lg:basis-[calc((100%-60px)/4)] lg:pl-5'
              >
                <ResponsiveModal>
                  <ResponsiveModalTrigger asChild>
                    <button
                      type='button'
                      aria-label={`${title} - ${t('cta')}`}
                      ga-tracking-value={`${TRACKING}__${index + 1}`}
                      className='group relative flex aspect-[260/335] w-full cursor-pointer flex-col justify-end gap-2 overflow-hidden rounded-lg bg-neutral-100 px-5 pb-5 text-left lg:aspect-[435/560] lg:px-7 lg:pb-7'
                    >
                      <Image
                        src={ImageHelper.getImageUrl(item.image, '', 870)}
                        alt={title}
                        fill
                        sizes='(min-width: 1024px) 23vw, 260px'
                        className='object-cover transition-transform duration-300 group-hover:scale-105'
                      />
                      <div className={IMAGE_GRADIENT_CLASS} />
                      <h3 className='relative font-criteria text-lg font-semibold leading-[26px] tracking-[-0.02em] text-white lg:text-2xl lg:leading-[35px]'>
                        {title}
                      </h3>
                      <span className='relative flex w-fit items-center gap-1 border-b border-neutral-50 py-1 font-criteria text-xs text-white transition-opacity group-hover:opacity-80 lg:gap-2 lg:text-base'>
                        {t('cta')}
                        <ArrowRight className='size-3.5 transition-transform group-hover:translate-x-0.5 lg:size-4' />
                      </span>
                    </button>
                  </ResponsiveModalTrigger>
                  <ResponsiveModalContent
                    className='gap-0 border-none px-5 py-6 lg:h-[600px] lg:max-h-none lg:w-[1000px] lg:max-w-none lg:rounded-md lg:px-9 lg:py-10'
                    aria-describedby={undefined}
                  >
                    <ResponsiveModalTitle className='hidden' />
                    <ResponsiveModalDescription className='hidden' />
                    <PreviewProductsModalContent
                      productIds={item.productIds}
                      thumbnail={item.image}
                      trackingValue={`${TRACKING}__${index + 1}`}
                    />
                  </ResponsiveModalContent>
                </ResponsiveModal>
              </CarouselItem>
            )
          })}
        </CarouselContent>

        <CarouselPrevious
          hideIfStart
          variant='white'
          className={cn(NAV_BUTTON_CLASS, 'left-2 lg:left-9')}
          ga-tracking-value={`${TRACKING}__prev`}
        />
        <CarouselNext
          hideIfEnd
          variant='white'
          className={cn(NAV_BUTTON_CLASS, 'right-[52px] lg:right-[96px]')}
          ga-tracking-value={`${TRACKING}__next`}
        />
      </Carousel>

      <div className='relative h-px bg-neutral-200 lg:h-0.5'>
        <div
          className='absolute inset-y-0 bg-black transition-[left] duration-150'
          style={{ width: `${thumbWidth}%`, left: `${thumbOffset}%` }}
        />
      </div>
    </section>
  )
}

/** Thanh tiến trình: độ rộng = tỉ lệ vùng nhìn thấy, vị trí theo scrollProgress của embla */
function useScrollThumb(api: CarouselApi) {
  const [state, setState] = useState({ thumbWidth: 0, thumbOffset: 0 })

  useEffect(() => {
    if (!api) return

    const update = () => {
      const snaps = api.scrollSnapList().length
      const thumbWidth = snaps > 1 ? 100 / snaps : 100
      const progress = Math.min(Math.max(api.scrollProgress(), 0), 1)
      setState({ thumbWidth, thumbOffset: progress * (100 - thumbWidth) })
    }

    update()
    api.on('scroll', update).on('reInit', update).on('resize', update)
    return () => {
      api.off('scroll', update).off('reInit', update).off('resize', update)
    }
  }, [api])

  return state
}
